import { useState } from "react";
import { Badge } from "@component/badge";
import { Button } from "@component/button";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@component/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import {
    BriefcaseIcon,
    BuildingIcon,
    CalendarClockIcon,
    ClockIcon,
    CopyIcon,
    EyeIcon,
    LockIcon,
    MapPinIcon,
    MessageCircleIcon,
    MoreVerticalIcon,
    PauseIcon,
    PencilIcon,
    PlayIcon,
    RefreshCwIcon,
    ShareIcon,
    TimerIcon,
    TrashIcon,
    UserCheck,
    UserPlus,
    UsersIcon,
} from "lucide-react";
import { Job } from "@type/job";
import { formatHumanReadable, parseFormat } from "@util/date";
import { useNavigate } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import { getStatusBadgeClass, shortLocation } from "@util/string";
import NotificationMessage from "./notification-message";
import { Tooltip } from "./tooltip";
import { getSubdomainHost } from "@util/name";
import VersionHistoryDialog from "./job-version-history-dialog";
import CloseJobConfirmationDialog from "@container/dialog/dialog-close-job-confirm";
import WhatsappAdLinkDialog from "@container/dialog/dialog-whatsapp-ad-link";
import DeleteJobConfirmationDialog from "@container/dialog/dialog-delete-job-confirm";
import JobNotifyCandidateDialog from "./job-notify-candidate-dialog";
import { SendCustomizedEmailRequest } from "@type/candidate";
import { sendCustomizedEmailV2 } from "@service/employer";
import { duplicateJob } from "@service/job";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { useSubscription } from "@context/subscription_context";

interface JobCardProps {
    job: Job;
    onJobAction: (job: Job) => void;
    onStatusChange: (jobId: string, status: string, is_public?: boolean | undefined) => void;
    onCloseJob: (job: Job) => void;
    onDelete: (job: Job) => void
}

export function JobCard({ job, onJobAction, onStatusChange, onCloseJob, onDelete }: JobCardProps) {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const { showTrialUsageLimitDialog, subscriptionActive, isWorkspaceRestricted, isJobActionBlocked } = useSubscription();
    // Trial-only: paid plans ignore CV/AI limit gates entirely.
    const isJobLimitReached = !!subscriptionActive?.is_trial && (!!subscriptionActive?.is_cv_limit_reached || !!subscriptionActive?.is_ai_limit_reached);
    const expiredTooltip = isWorkspaceRestricted ? "Your trial has expired. Upgrade to manage jobs." : undefined;
    const [isOpenCloseJobDialog, setIsOpenCloseJobDialog] = useState<boolean>(false);
    const [isOpenDeleteJobDialog, setIsOpenDeleteJobDialog] = useState<boolean>(false);
    const [isOpenNotifyCandidateDialog, setIsOpenNotifyCandidateDialog] = useState<boolean>(false);
    const [notificationType, setNotificationType] = useState<string>("")
    const [notificationMessage, setNotificationMessage] = useState<string>("")
    const [isOpenVersionHistoryDialog, setIsOpenVersionHistoryDialog] = useState<boolean>(false);
    // WhatsApp ad link (#3088) — only for jobs whose company has the hiring flow enabled.
    // #3251: "Copy WhatsApp link" opens the ad-link dialog (link + prefilled
    // message preview + copy) — whatsapp_first jobs only.
    const [isOpenWhatsappAdLinkDialog, setIsOpenWhatsappAdLinkDialog] = useState<boolean>(false);
    const [isDuplicating, setIsDuplicating] = useState<boolean>(false);

    // #3443 (BE #4483): clone the job into a new listing (created Active right
    // away, per product decision), then land HR on its edit page to rename the copy.
    const handleDuplicate = async () => {
        if (isDuplicating) return;
        setIsDuplicating(true);
        try {
            const newJobId = await duplicateJob(job.id);
            if (newJobId) {
                snackbar.showSuccess("Listing duplicated");
                navigate(`/jobs/edit/${newJobId}`);
            } else {
                snackbar.showError("Failed to duplicate listing. Please try again later.");
            }
        } catch (error) {
            console.error("Failed to duplicate job:", error);
            snackbar.showError("Failed to duplicate listing. Please try again later.");
        } finally {
            setIsDuplicating(false);
        }
    };

    const onShare = async () => {
        setNotificationType("")
        setNotificationMessage("")
        const candidateProfileLink = `${getSubdomainHost(job.company_subdomain || "")}/job/${job.id}`;
        // Fallback to copying to clipboard
        try {
            await navigator.clipboard.writeText(candidateProfileLink);
            setNotificationType("success")
            setNotificationMessage(`Job ${job.job_title} copied to clipboard.`)
        } catch (error) {
            console.error('Error copying to clipboard:', error);
        }
    }

    const handleSendNotifyCandidate = async (body: SendCustomizedEmailRequest) => {
        sendCustomizedEmailV2(body).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(resp.data.message);
                onCloseJob(job);
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send interview invitation: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            }
        }).finally(() => {
            setIsOpenNotifyCandidateDialog(false);
        });
    };

    return (
        <>
            {notificationType !== "" && (
                <NotificationMessage
                    duration={15000}
                    type={notificationType}
                    message={notificationMessage}
                />
            )}
            <Card className="h-full">
                <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                        <CardTitle className="text-lg font-semibold">
                            {job.job_title}
                        </CardTitle>
                        <div className="flex flex-row align-middle items-center justify-items-end space-x-2">
                            {["admin", "super_admin"].includes(userInfo?.role || "") && job.is_byoc && (
                                <Badge
                                    variant="secondary"
                                    className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800 whitespace-nowrap"
                                    style={{
                                        paddingTop: "4px",
                                        paddingBottom: "4px",
                                        borderRadius: "1rem",
                                    }}
                                >
                                    BYOC
                                </Badge>
                            )}
                            {(job.is_collaborator && job.job_owner) && (
                                <Tooltip
                                    content={
                                        <p>
                                            Shared by {job.job_owner}
                                        </p>
                                    }
                                >
                                    <div className="flex items-center justify-center w-6 h-6 bg-blue-50 border border-blue-200 rounded-full cursor-help hover:bg-blue-100 transition-colors">
                                        <UserCheck className="h-3 w-3 text-blue-600" />
                                    </div>
                                </Tooltip>
                            )}

                            {(!job.is_collaborator && (job.collaborators || []).length > 0) && (
                                <Tooltip
                                    content={
                                        <p>
                                            Shared with {(job.collaborators || []).length > 1 ? `${(job.collaborators || [])[0]} and ${(job.collaborators || []).slice(1).length} others` : (job.collaborators || [])[0]}
                                        </p>
                                    }
                                >
                                    <div className="flex items-center justify-center w-6 h-6 bg-purple-50 border border-purple-200 rounded-full cursor-help hover:bg-purple-100 transition-colors">
                                        <UserPlus className="h-3 w-3 text-purple-600" />
                                    </div>
                                </Tooltip>
                            )}

                            {job.is_enable_auto_rejection && (
                                <Tooltip
                                    delayDuration={100}
                                    content={
                                        <p>Auto-rejection is enabled for this job.</p>
                                    }
                                >
                                    <div
                                        className="flex items-center justify-center w-6 h-6 bg-red-50 border border-red-200 rounded-full cursor-help hover:bg-red-100 transition-colors"
                                        style={{ border: "0.909091px solid #fecaca" }}
                                    >
                                        <TimerIcon className="h-3 w-3 text-red-600" />
                                    </div>
                                </Tooltip>
                            )}
                            <Badge
                                className={`!shadow-none rounded-xl ${getStatusBadgeClass(job.job_status === "Published" ? "Active" : job.job_status === "Deleted" ? "Closed" : job.job_status)}`}
                            >
                                {job.job_status === "Published" ? "Active" : job.job_status === "Deleted" ? "Closed" : job.job_status}
                            </Badge>
                            {(!isLoadingUserProfile && job.job_status !== "Deleted" && job.job_status !== "Closed") && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <MoreVerticalIcon className="h-4 w-4" />
                                            <span className="sr-only">
                                                More options
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        {!job.is_collaborator && userInfo?.permission !== "member" && (
                                            <DropdownMenuItem
                                                className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => onJobAction(job)}
                                                disabled={isJobActionBlocked}
                                                title={isJobActionBlocked ? expiredTooltip : undefined}
                                            >
                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                Edit listing
                                            </DropdownMenuItem>
                                        )}
                                        {!job.is_collaborator && userInfo?.permission !== "member" && (
                                            <DropdownMenuItem
                                                className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => handleDuplicate()}
                                                disabled={isWorkspaceRestricted || isDuplicating}
                                                title={isWorkspaceRestricted ? expiredTooltip : undefined}
                                            >
                                                <CopyIcon className="h-4 w-4 mr-2" />
                                                {isDuplicating ? "Duplicating…" : "Duplicate listing"}
                                            </DropdownMenuItem>
                                        )}
                                        {job.is_collaborator && (
                                            <DropdownMenuItem className="cursor-pointer" onClick={() => onJobAction(job)}>
                                                <EyeIcon className="h-4 w-4 mr-2" />
                                                View listing
                                            </DropdownMenuItem>
                                        )}
                                        {!job.is_collaborator && userInfo?.permission !== "member" && (
                                            <>
                                                {(job.job_status === "Active" || job.job_status == "Published") && ["hr", "super_admin"].includes(userInfo?.role || "") && (
                                                    <DropdownMenuItem
                                                        className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => onStatusChange(job.id, "Paused")}
                                                        disabled={isWorkspaceRestricted}
                                                        title={expiredTooltip}
                                                    >
                                                        <PauseIcon className="h-4 w-4 mr-2" />
                                                        Pause listing
                                                    </DropdownMenuItem>
                                                )}
                                                {job.job_status === "Paused" && ["hr", "super_admin"].includes(userInfo?.role || "") && !subscriptionActive?.is_cv_screening_limit_reached && (
                                                    <DropdownMenuItem
                                                        className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => onStatusChange(job.id, "Published")}
                                                        disabled={isJobLimitReached || isWorkspaceRestricted}
                                                        title={isWorkspaceRestricted ? expiredTooltip : undefined}
                                                    >
                                                        <PlayIcon className="h-4 w-4 mr-2" />
                                                        Activate listing
                                                    </DropdownMenuItem>
                                                )}
                                            </>
                                        )}
                                        {/* #3251: WhatsApp-first jobs get "Copy WhatsApp link" INSTEAD of
                                            "Copy listing URL" (candidates enter via WhatsApp, not the web
                                            listing); every other job keeps the listing URL. */}
                                        {job.hiring_flow?.channel === "whatsapp_first" ? (
                                            <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={() => setIsOpenWhatsappAdLinkDialog(true)}
                                            >
                                                <MessageCircleIcon className="h-4 w-4 mr-2 text-green-600" />
                                                Copy WhatsApp link
                                            </DropdownMenuItem>
                                        ) : (
                                            <DropdownMenuItem className="cursor-pointer" onClick={() => onShare()}>
                                                <ShareIcon className="h-4 w-4 mr-2" />
                                                Copy listing URL
                                            </DropdownMenuItem>
                                        )}
                                        {!job.is_collaborator && (
                                            <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={() => setIsOpenVersionHistoryDialog(true)}
                                            >
                                                <RefreshCwIcon className="h-4 w-4 mr-2" />
                                                Show version history
                                            </DropdownMenuItem>
                                        )}
                                        {!job.is_collaborator && (["hr", "super_admin"].includes(userInfo?.role || "") || userInfo?.permission !== "member") && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={() => setIsOpenCloseJobDialog(true)}
                                                    disabled={isWorkspaceRestricted}
                                                    title={expiredTooltip}
                                                    className="text-red-600 focus:text-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <TrashIcon className="h-4 w-4 mr-2" />
                                                    Close listing
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                            {(!isLoadingUserProfile && (job.job_status === "Deleted" || job.job_status === "Closed")) && !job.is_collaborator && (["hr", "super_admin"].includes(userInfo?.role || "") || userInfo?.permission !== "member") && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                        >
                                            <MoreVerticalIcon className="h-4 w-4" />
                                            <span className="sr-only">
                                                More options
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={() => setIsOpenDeleteJobDialog(true)}
                                            className="text-red-600 focus:text-red-600 cursor-pointer"
                                        >
                                            <TrashIcon className="h-4 w-4 mr-2" />
                                            Delete listing
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <BriefcaseIcon className="h-4 w-4" />
                        <span>{job.department || "No Department"}</span>
                    </div>
                    {job.company_name && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <BuildingIcon className="h-4 w-4" />
                            {/* WhatsApp-first flow marker — candidates run this job's flow in WA. */}
                            {job.hiring_flow?.channel === "whatsapp_first" && (
                                <Tooltip content="WhatsApp-first hiring flow">
                                    <MessageCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                </Tooltip>
                            )}
                            <span>{job.company_name}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPinIcon className="h-4 w-4" />
                        <span>{shortLocation(job.location) || "No Location"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <UsersIcon className="h-4 w-4" />
                        <span>{job.applicants_count} {job.applicants_count > 1 ? "applicants" : "applicant"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CalendarClockIcon className="h-4 w-4" />
                        <span>{job.job_type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ClockIcon className="h-4 w-4" />
                        <span>Posted {formatHumanReadable(job.created_at, true)}</span>
                    </div>
                    {job.updated_at && (
                        <div
                            className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors group"
                            onClick={() => setIsOpenVersionHistoryDialog(true)}
                        >
                            <RefreshCwIcon className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
                            <span className="underline decoration-dotted underline-offset-4">
                                Last updated {formatHumanReadable(job.updated_at, true)}
                            </span>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="pt-2">
                    <Button
                        variant="outline"
                        className={`w-full border-black hover:bg-blue-600 hover:text-white hover:border-blue-800`}
                        onClick={() => navigate(`/jobs/${job.id}/applications`)}
                    >
                        View Applicants
                    </Button>
                </CardFooter>
            </Card>

            <VersionHistoryDialog
                job={job}
                isOpen={isOpenVersionHistoryDialog}
                onClose={() => setIsOpenVersionHistoryDialog(false)}
            />

            <WhatsappAdLinkDialog
                isOpen={isOpenWhatsappAdLinkDialog}
                jobId={job.id}
                onClose={() => setIsOpenWhatsappAdLinkDialog(false)}
            />


            <CloseJobConfirmationDialog
                isOpen={isOpenCloseJobDialog}
                countApplicants={job.applicants_count || 0}
                onClose={() => setIsOpenCloseJobDialog(false)}
                onConfirmWithoutEmail={() => {
                    setIsOpenCloseJobDialog(false)
                    onCloseJob(job)
                }}
                onConfirmWithEmail={() => {
                    setIsOpenCloseJobDialog(false)
                    setIsOpenNotifyCandidateDialog(true)
                }}
            />

            <JobNotifyCandidateDialog
                isOpen={isOpenNotifyCandidateDialog}
                job={job}
                onClose={() => setIsOpenNotifyCandidateDialog(false)}
                onSendNotification={handleSendNotifyCandidate}
            />

            <DeleteJobConfirmationDialog
                isOpen={isOpenDeleteJobDialog}
                job={job}
                onClose={() => setIsOpenDeleteJobDialog(false)}
                onConfirm={(job: Job) => {
                    setIsOpenDeleteJobDialog(false);
                    snackbar.showSuccess("Job listing successfully deleted.");
                    onDelete(job);
                }}
            />
        </>
    );
}
